import React, { useMemo } from 'react'
import imageUrlBuilder from '@sanity/image-url'
import Autoscroll from 'embla-carousel-auto-scroll'
import useEmblaCarousel from 'embla-carousel-react'
// @ts-expect-error
import sanityClient from '@sanity/client'
import './care-journey.css'

interface CareJourneyCard {
  _key?: string
  caption: string
  image: {
    asset: {
      _id?: string
      _ref?: string
    }
  }
  link: string
}

interface CareJourneyProps {
  title?: string
  cards: CareJourneyCard[]
}

const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_DATASET,
  useCdn: false,
})

const CareJourney: React.FC<{ data: CareJourneyProps }> = ({ data }) => {
  const { title, cards } = data
  const builder = imageUrlBuilder(client)

  const getImageUrl = (asset: any) => {
    if (!asset) return ''
    return builder.image(asset).url()
  }

  const autoScroll = useMemo(
    () =>
      Autoscroll({
        speed: 0.5,
        stopOnInteraction: true,
      }),
    []
  )

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
    },
    [autoScroll]
  )

  const scrollPrev = () => {
    autoScroll.stop()
    emblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    autoScroll.stop()
    emblaApi?.scrollNext()
  }

  return (
    <div className="care-journey__container">
      {title && <h2 className="care-journey__title">{title}</h2>}

      <div className="care-journey__carousel">
        <div ref={emblaRef} className="care-journey__viewport">
          <div className="care-journey__container-inner">
            {cards.map((card, index) => {
              const key = `${card._key || card.caption}-${index}`

              const imageUrl = getImageUrl(card.image?.asset)

              return (
                <div className="care-journey__slide" key={key}>
                  <a
                    href={card.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="care-journey__slide-inner"
                    style={{
                      backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url(${imageUrl})`,
                    }}
                  >
                    <span className="care-journey__caption">
                      {card.caption}
                    </span>
                  </a>
                </div>
              )
            })}
          </div>
        </div>

        <div className="care-journey__arrows">
          <button
            type="button"
            aria-label="Scroll Left"
            onClick={() => scrollPrev()}
            className="care-journey__arrow"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.396 6.7925L13.1035 5.5L7.60352 11L13.1035 16.5L14.396 15.2075L10.1977 11L14.396 6.7925Z"
                fill="#282725"
              />
            </svg>
          </button>

          <button
            type="button"
            aria-label="Scroll Right"
            onClick={() => scrollNext()}
            className="care-journey__arrow"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.89602 5.5L7.60352 6.7925L11.8018 11L7.60352 15.2075L8.89602 16.5L14.396 11L8.89602 5.5Z"
                fill="#282725"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

export { CareJourney }
export type { CareJourneyProps, CareJourneyCard }
